import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ChevronRight, ArrowLeft, CheckCircle2, UserCheck, ShieldAlert, Laptop, DollarSign } from 'lucide-react'
import Layout from '@/components/layout/Layout'
import MainHeading from '@/components/common/MainHeading'
import { API_URL } from '@/api/constant'
import { RoutesLink } from '@/utility/RouteLinks'
import { showToastNotification } from '@/utility/helper'
import EmployeeOnBoardService from '@/services/EmployeeOnBoard/EmployeeOnBoardService'
import OffboardingService from '@/services/Offboarding/OffboardingService'
import type { IUser } from '@/interface/employeeOnBoard'
import type { IOffboardingDetails, IOffboardingProcessStep } from '@/interface/offboarding.interface'

// Import Steps components
import ExitDetailsStep from '../OffboardSteps/ExitDetailsStep'
import AssetClearanceStep from '../OffboardSteps/AssetClearanceStep'
import AccountClearanceStep from '../OffboardSteps/AccountClearanceStep'
// import { useCurrentUser } from '@/hooks/useCurrentUser'

const STEPS = [
    { id: 'exit-details', title: 'Exit Details', icon: ShieldAlert },
    { id: 'assets', title: 'Asset Clearance', icon: Laptop },
    { id: 'accounts', title: 'Account Clearance', icon: DollarSign }
]

const ExitClearanceDetail: React.FC = () => {
    const { id } = useParams<{ id?: string }>()
    // const { userId } = useCurrentUser();
    const location = useLocation();
    const offBoardId = location?.state?.offboardingId;
    const navigate = useNavigate()

    const [isLoading, setIsLoading] = useState(false)
    const [activeTab, setActiveTab] = useState('exit-details')
    const [selectedEmployee, setSelectedEmployee] = useState<IUser | null>(null)
    const [initialExitData, setInitialExitData] = useState<IOffboardingDetails | null>(null)
    const [isCompleted, setIsCompleted] = useState(false)

    // Clearance Process checklist loaded from server
    const [clearanceSteps, setClearanceSteps] = useState<IOffboardingProcessStep[]>([
        { key: "asset-return", title: "Asset Return", status: "PENDING" },
        { key: "account-clearance", title: "Account Clearance", status: "PENDING" }
    ])

    // Load offboarding details on mount
    useEffect(() => {
        if (!id || !offBoardId) {
            navigate(RoutesLink.exitClearanceList)
            return
        }

        const loadDetails = async () => {
            setIsLoading(true)
            try {
                // Get general employee details
                const empRes = await EmployeeOnBoardService.getEmployeeById(id)
                if (empRes?.status && empRes?.data) {
                    setSelectedEmployee(empRes.data)
                }

                // Get offboarding specific progress details
                const offRes = await OffboardingService.getOffboardingDetails(offBoardId)
                if (offRes?.status && offRes?.data) {
                    const data = offRes.data.offboarding
                    setInitialExitData({
                        lastWorkingDate: data.lastWorkingDate ? data.lastWorkingDate.split("T")[0] : "",
                        resignationDate: data.resignationDate ? data.resignationDate.split("T")[0] : "",
                        reason: data.reason || "Resignation",
                        noticePeriod: data.noticePeriod || "",
                        relievingDate: data.relievingDate ? data.relievingDate.split("T")[0] : "",
                        remarks: data.remarks || "",
                        handoverToUserId: data.handoverToUser?.id || "",
                        // handoverToUser: data.handoverToUser,
                        status: data?.status
                    })
                    if (data.offboardingProcess && data.offboardingProcess.length > 0) {
                        setClearanceSteps(data.offboardingProcess)
                    }
                    setIsCompleted(data?.status == "Completed" || data?.status == "COMPLETED" ? true : false)
                }
            } catch (err) {
                console.error("Error loading offboarding details:", err)
                showToastNotification("danger", "No existing offboarding process found for this user.")
            } finally {
                setIsLoading(false)
            }
        }
        loadDetails()
    }, [id, offBoardId, navigate])

    return (
        <Layout>
            <div className="space-y-6 max-w-4xl mx-auto pb-12">
                {/* Back button */}
                <div
                    className="flex items-center gap-2 text-slate-500 hover:text-slate-700 cursor-pointer transition-colors"
                    onClick={() => navigate(RoutesLink.exitClearanceList)}
                >
                    <ArrowLeft size={16} />
                    <span className="text-sm font-semibold">Back to Exit Clearance list</span>
                </div>

                <MainHeading
                    title='Exit Clearance Verification'
                />

                {/* Profile Overview Card */}
                {selectedEmployee && (
                    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-6">
                        <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                            <UserCheck className="w-5 h-5 text-primary" />
                            Employee Exit Overview
                        </h3>
                        <div className="flex items-center gap-4 p-4 bg-slate-50/50 border border-slate-100 rounded-2xl">
                            {selectedEmployee?.profilePhoto ? (
                                <img src={`${API_URL}/${selectedEmployee.profilePhoto}`} alt="" className="h-14 w-14 rounded-full object-cover border border-white shadow-sm" />
                            ) : (
                                <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shadow-sm">
                                    {selectedEmployee?.name?.charAt(0)?.toUpperCase()}
                                </div>
                            )}
                            <div>
                                <h4 className="font-bold text-slate-900 text-lg">{selectedEmployee?.name}</h4>
                                <p className="text-sm text-slate-500">{selectedEmployee?.email} • {selectedEmployee?.designation?.name || 'Staff'}</p>
                                <p className="text-xs text-primary font-semibold mt-1">Site: {selectedEmployee?.site?.name || 'N/A'}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Steps renderer */}
                {selectedEmployee && (
                    <div className="space-y-6">
                        {/* Stepper Header tab bar */}
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 overflow-x-auto">
                            <div className="flex items-center justify-between min-w-[600px] gap-2">
                                {STEPS.map((step, idx) => {
                                    const isActive = activeTab === step.id
                                    const isDone = isCompleted ||
                                        (idx === 0 && !!id) ||
                                        (idx === 1 && clearanceSteps.find(s => s.key === "asset-return")?.status === "COMPLETED") ||
                                        (idx === 2 && clearanceSteps.find(s => s.key === "account-clearance")?.status === "COMPLETED")

                                    return (
                                        <React.Fragment key={step.id}>
                                            <button
                                                onClick={() => setActiveTab(step.id)}
                                                className={`flex items-center gap-2.5 py-2.5 px-4 rounded-xl transition-all ${isActive
                                                    ? 'bg-primary text-white shadow-md font-bold'
                                                    : 'text-slate-500 hover:bg-slate-50 font-medium'
                                                    }`}
                                            >
                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs shrink-0 ${isActive
                                                    ? 'bg-white/20 text-white'
                                                    : isDone
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-slate-100 text-slate-600'
                                                    }`}>
                                                    {isDone ? <CheckCircle2 className="w-4.5 h-4.5" /> : idx + 1}
                                                </div>
                                                <span className="text-sm truncate">{step.title}</span>
                                            </button>
                                            {idx < STEPS.length - 1 && (
                                                <ChevronRight className="text-slate-300 w-4 h-4 shrink-0" />
                                            )}
                                        </React.Fragment>
                                    )
                                })}
                            </div>
                        </div>

                        {/* Current step card content */}
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
                            {activeTab === 'exit-details' && (
                                <ExitDetailsStep
                                    id={offBoardId || ""}
                                    isLoading={isLoading}
                                    setIsLoading={setIsLoading}
                                    userId={id || ""}
                                    onNext={() => setActiveTab('assets')}
                                    isCompleted={true}
                                    initialData={initialExitData}
                                />
                            )}

                            {activeTab === 'assets' && (
                                <AssetClearanceStep
                                    isLoading={isLoading}
                                    setIsLoading={setIsLoading}
                                    userId={offBoardId || ""}
                                    onNext={() => setActiveTab('accounts')}
                                    clearanceSteps={clearanceSteps}
                                    setClearanceSteps={setClearanceSteps}
                                    isCompleted={isCompleted}

                                />
                            )}

                            {activeTab === 'accounts' && (
                                <AccountClearanceStep
                                    isLoading={isLoading}
                                    setIsLoading={setIsLoading}
                                    userId={offBoardId || ""}
                                    onNext={() => navigate(RoutesLink.exitClearanceList)}
                                    clearanceSteps={clearanceSteps}
                                    setClearanceSteps={setClearanceSteps}
                                    isCompleted={isCompleted}
                                    exitClearance={true}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    )
}

export default ExitClearanceDetail
